import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { updateSubscriptionPrice } from 'services/endpoints/settings/general/subscription'
import { useUserProfile } from 'store'
import { type ICreatorPriceLimits } from 'types/interfaces/IProfile'

import style from './_set-price.module.scss'
import { PriceCard } from '../components/PriceCard'

export const SetSubscriptionPrice = () => {
    const { t } = useTranslation()
    const userData = useUserProfile()!
    const queryClient = useQueryClient()

    const priceLimits =
        userData?.price_limits as ICreatorPriceLimits | undefined

    const minSubscriptionPrice =
        priceLimits?.min_subscription_price

    const maxSubscriptionPrice =
        priceLimits?.max_subscription_price

    const hasPriceLimits =
        minSubscriptionPrice != null &&
        maxSubscriptionPrice != null

    const [price, setPrice] = useState<any>(
        Number(userData?.default_subscription_price ?? 0) === 0
            ? t('Free')
            : userData?.default_subscription_price ?? t('priceNotSet')
    )

    const priceMutation = useMutation(
        async (newPrice: number) =>
            await updateSubscriptionPrice(newPrice),
        {
            onSuccess: data => {
                queryClient.invalidateQueries('userProfile')
                queryClient.invalidateQueries('fans-groups')

                if (data?.price !== undefined) {
                    setPrice(
                        Number(data.price) === 0
                            ? t('Free')
                            : data.price
                    )
                }
            },
            onError: () => {
                addToast(
                    'error',
                    'Something went wrong, please try again'
                )
            }
        }
    )

    const submitFn = () => {
        if (!hasPriceLimits) {
            addToast(
                'error',
                'Subscription price limits are unavailable'
            )
            return
        }

        const rawPrice = String(price).trim()
        const normalizedPrice = rawPrice.toLowerCase()
        const translatedFree = t('Free').trim().toLowerCase()
        const onlyNumberRegex = /^\d+(\.\d{1,2})?$/

        const isFree =
            normalizedPrice === translatedFree ||
            normalizedPrice === 'free'

        if (isFree) {
            priceMutation.mutate(0)
            return
        }

        if (!onlyNumberRegex.test(rawPrice)) {
            addToast(
                'error',
                t('inputValidPrice')
            )
            return
        }

        const numericPrice = Number(rawPrice)
        const minPrice = Number(minSubscriptionPrice)
        const maxPrice = Number(maxSubscriptionPrice)

        if (
            Number.isNaN(minPrice) ||
            Number.isNaN(maxPrice)
        ) {
            addToast(
                'error',
                'Subscription price limits are unavailable'
            )
            return
        }

        if (numericPrice === 0) {
            priceMutation.mutate(0)
            return
        }

        if (numericPrice < minPrice) {
            addToast(
                'error',
                `${t('minPriceIs')} $${minPrice}`
            )
            return
        }

        if (numericPrice > maxPrice) {
            addToast(
                'error',
                `${t('maxPriceIs')} $${maxPrice}`
            )
            return
        }

        priceMutation.mutate(numericPrice)
    }

    const isSaveDisabled =
        priceMutation.isLoading ||
        !hasPriceLimits

    return (
        <WithHeaderSection
            customClass={style.set_subscription_price}
            headerSection={
                <LayoutHeader
                    type='basic'
                    section={t('subscriptions')}
                    title={t('setPrice')}
                />
            }
        >
            <div className={style.price_content}>
                <PriceCard
                    price={price}
                    updatePrice={(value: any) => {
                        setPrice(value)
                    }}
                    label={t('pricingPlan')}
                    type='text'
                    parseValue={false}
                    isValid
                    customClass={style.price_card}
                />

                <div className={style.price_description}>
                    <div>
                        {hasPriceLimits ? (
                            <>
                                {t('minimum')}{' '}
                                <span>
                                    ${minSubscriptionPrice}
                                </span>{' '}
                                {t('or')}{' '}
                                <span>{t('Free')}</span>
                                , {t('maximum')}{' '}
                                <span>
                                    ${maxSubscriptionPrice}
                                </span>
                            </>
                        ) : (
                            <span>
                                Subscription price limits are unavailable.
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className={style.button_wrapper}>
                <Button
                    text={
                        priceMutation.isLoading ? (
                            <BeatLoader size={7} />
                        ) : (
                            t('save')
                        )
                    }
                    color='black'
                    disabled={isSaveDisabled}
                    customClass={`${style.button} ${
                        isSaveDisabled
                            ? style.button_disabled
                            : ''
                    }`}
                    clickFn={submitFn}
                />
            </div>
        </WithHeaderSection>
    )
}